<template>
  <AppModalDialog ref="dialogRef" :hash="PROFILE_HASH" :style="contentStyle" :traps-focus="true">
    <template #header-left>
      <div v-if="isSignedIn" class="header-left">
        <AppModalProfileRoleLinks :roles="roles" />
        <AppModalProfileTabNav
          :model-value="activeTab"
          :is-editor-or-admin="isEditorOrAdmin || isTesterView"
          :has-events="hasEvents"
          @update:model-value="openTab" />
      </div>
    </template>
    <div v-if="isSignedIn" class="tab-body">
      <div v-if="activeTab === 'settings'" ref="settingsRef" class="settings-tab">
        <AppModalDialogLoading :is-busy="!emailInClerk" busy-message="Loading…" />
        <div class="header">
          <AppModalProfileAvatar :key="user?.imageUrl" class="avatar" :user="user" show-add-label />
          <div class="identity">
            <AppModalProfileMeta editable is-settings>
              <template #name><AppModalProfileName :model-value="nameInClerk" /></template>
            </AppModalProfileMeta>
            <AppModalProfileEmail :model-value="emailInClerk" />
          </div>
        </div>
        <div class="settings">
          <!-- SUBSCRIPTIONS, PRIVACY, VIDEOS (2026-10-20, Seán's Design 1) -->
          <AppModalProfileSettingsSubscribe />
          <AppModalProfileSettingsPrivacy />
          <!-- Time Zone: hidden 2026-10-14 (the menus on /2026 and the Program now reach every
               zone); uncomment here and the import below to bring it back -->
          <!-- <AppModalProfileTimeZone /> -->
          <AppModalProfileVideos
            :video-access="videoAccess"
            :locked-counts="lockedCounts"
            :limited="limited" />
        </div>
      </div>
      <div v-else-if="activeTab === 'events'" class="page">
        <AppModalProfileEvents />
      </div>
      <div v-else-if="activeTab === 'public'" class="page fills">
        <AppModalDialogLoading :is-busy="isLoading || savingTab === 'profile'" :busy-message="savingTab === 'profile' ? 'Saving…' : 'Loading…'" />
        <AppModalProfileEditor />
      </div>
      <div v-else-if="activeTab === 'testimonial'" class="page fills">
        <AppModalDialogLoading :is-busy="isLoading || savingTab === 'testimonial'" :busy-message="savingTab === 'testimonial' ? 'Saving…' : 'Loading…'" />
        <AppModalProfileTestimonialEditor />
      </div>
    </div>
    <template #footer>
      <!-- every tab: Sign Out (left) and Close (right, the usual action), or Unlock Videos when
           there are videos this account can't play yet (useLockedVideos.js; the ✕ still
           closes; 2026-10-20, Design 2); while the bio or
           testimonial is edited, Cancel (left) and OK (right). Settings' edits have their own
           Cancel and OK in the edit layer (EditButtons), so Sign Out and Close stay. The status
           floats just above the buttons, so it never moves them or the content -->
      <div class="footer-buttons compact">
        <span v-if="saveStatus === 'success'" class="save-status success">Saved</span>
        <span v-else-if="saveStatus === 'error'" class="save-status error">Save failed</span>
        <template v-if="isEditOpen">
          <ButtonBespoke class="footer-button" data-profile-cancel outstanding @click="cancelEdits">
            Cancel
          </ButtonBespoke>
          <ButtonBespoke
            class="footer-button"
            data-profile-commit
            outstanding
            :disabled="isConfirmDisabled"
            @click="confirmEdits">
            {{ isCommittingFields || savingTab ? "Saving…" : "OK" }}
          </ButtonBespoke>
        </template>
        <template v-else>
          <ButtonBespoke
            class="footer-button isSecondary"
            data-profile-exit
            :shadow="false"
            @click="signOut">
            Sign Out
          </ButtonBespoke>
          <ButtonBespoke
            v-if="hasLockedVideos"
            class="footer-button"
            data-profile-unlock
            outstanding
            :shadow="false"
            @click="openUnlock">
            Unlock Videos
          </ButtonBespoke>
          <ButtonBespoke
            v-else
            class="footer-button"
            data-profile-exit
            outstanding
            :shadow="false"
            @click="closeDialog">
            Close
          </ButtonBespoke>
        </template>
      </div>
    </template>
  </AppModalDialog>
</template>

<script setup>
import {
  TEMPORARY_FIELDS, getVideoAccessNow, getIsTemporaryValid, getTemporaryUntil,
} from "@/composables/videoAccessNow";
import {computed, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import AppModalDialog from "@/components/App/Modal/Dialog/index";
import AppModalDialogLoading from "@/components/App/Modal/Dialog/Loading";
import {useInitialFocus} from "@/composables/useInitialFocus";
import ButtonBespoke from "@/components/ButtonBespoke";
import AppModalProfileAvatar from "@/components/App/Modal/Profile/Avatar";
import AppModalProfileName from "@/components/App/Modal/Profile/Name";
import AppModalProfileMeta from "@/components/App/Modal/Profile/ProfileMeta";
import AppModalProfileEmail from "@/components/App/Modal/Profile/Email";
import AppModalProfileSettingsSubscribe from "@/components/App/Modal/Profile/SettingsSubscribe";
import AppModalProfileSettingsPrivacy from "@/components/App/Modal/Profile/SettingsPrivacy";
// import AppModalProfileTimeZone from "@/components/App/Modal/Profile/TimeZone";
import AppModalProfileVideos from "@/components/App/Modal/Profile/Videos";
import AppModalProfileTabNav from "@/components/App/Modal/Profile/TabNav";
import AppModalProfileRoleLinks from "@/components/App/Modal/Profile/RoleLinks";
import AppModalProfileEvents from "@/components/App/Modal/Profile/Events";
import AppModalProfileEditor from "@/components/App/Modal/Profile/Input";
import AppModalProfileTestimonialEditor from "@/components/App/Modal/Profile/TestimonialEditor";
import useUserSafe from "@/composables/useUserSafe";
import {useProfileContent} from "@/composables/useProfileContent";
import {useProfileFieldEdits} from "@/composables/useProfileFieldEdits";
import {useNameEdit} from "@/composables/useNameEdit";
import {useEmailEdit} from "@/composables/useEmailEdit";
import {authFetch, readJson} from "@/composables/useAuthFetch";
import {useSettingsHeight} from "@/composables/useSettingsHeight";
import {PROFILE_HASH, getIsProfileHash, useProfileTabs} from "@/composables/useProfileTabs";
import useAttendedEvents from "@/composables/useAttendedEvents";
import useLockedVideos from "@/composables/useLockedVideos";

const contentStyle = computed(() => ({
  maxHeight: "95dvh", // Settings is taller since (Dialog's cap: 700px); a short screen scrolls
  height: dialogHeight.value, // every tab: Settings' natural height (useSettingsHeight)
}));

const route = useRoute();
const router = useRouter();
const {isLoaded, isSignedIn, user} = useUserSafe();
const isEditorOrAdmin = ref(false);
const roles = ref({});
const hasFetchedRoles = ref(false);
// the Events tab only when there's something on it: events attended or registered for, or
// registrations paid for others
const {attendedEvents} = useAttendedEvents();
const hasEvents = computed(() => {
  return attendedEvents.value.length > 0 || Boolean(user.value?.publicMetadata?.purchases?.length);
});
// the tab follows the hash, e.g. #profile&events (from #register once registered for both)
const {activeTab, openTab, isTesterView} = useProfileTabs({
  route, router, user, isEditorOrAdmin, hasFetchedRoles, hasEvents
});

// #profile while signed out (e.g. a link from the fullstack, a stale tab): there's no profile to
// show, so it opens Sign In instead. Only once Clerk has loaded, so no one signed in is bounced.
watch([() => route.hash, isLoaded, isSignedIn], ([hash, loaded, signedIn]) => {
  if (!getIsProfileHash(hash) || !loaded || signedIn) return;
  router.replace({path: route.path, hash: "#sign-in"});
}, {immediate: true});

watch(isSignedIn, async (signedIn) => {
  hasFetchedRoles.value = false;
  roles.value = {};
  if (!signedIn) return (isEditorOrAdmin.value = false);
  const res = await authFetch("/api/v1/user/get-roles");
  roles.value = res.ok ? await readJson(res) : {};
  isEditorOrAdmin.value = Boolean(roles.value.isAdmin || roles.value.isEditor);
  hasFetchedRoles.value = true;
}, {immediate: true});
const {
  loadContent, saveContent, saveStatus, isLoading, isSaving, savingTab,
  isBioDirty, isTestimonialDirty, isBioOverLimit, isTestimonialOverLimit,
  isBioEditing, endBioEditing, discardBioEdits,
  isTestimonialEditing, endTestimonialEditing, discardTestimonialEdits,
} = useProfileContent();
const {isEditingName, discardName} = useNameEdit();
const {discardEmails} = useEmailEdit();

// homepage, LinkedIn or affiliation (Settings): their layer's OK commits them (ProfileMeta)
const {
  isCommitting: isCommittingFields, closeAll: closeAllFields, discardAll: discardAllFields,
} = useProfileFieldEdits();

// Public: save the bio if it's being edited; it closes once saved
async function confirmPublicEdits() {
  if (!isBioEditing.value) return;
  if (isBioDirty.value) await saveContent("profile");
  if (saveStatus.value !== "error") endBioEditing();
}

async function confirmTestimonialEdits() {
  if (isTestimonialDirty.value) await saveContent("testimonial");
  if (saveStatus.value !== "error") endTestimonialEditing();
}

const CONFIRM_BY_TAB = {
  public: confirmPublicEdits,
  testimonial: confirmTestimonialEdits
};

function confirmEdits() {
  CONFIRM_BY_TAB[activeTab.value]?.();
}

// Cancel: the open edits on this tab are thrown away (as Escape does); the dialog stays open.
// Settings' edits have their own Cancel (EditButtons); this drops them on Close and Sign Out
function cancelSettingsEdits() {
  discardAllFields();
  discardName();
  discardEmails();
}

const CANCEL_BY_TAB = {
  public: discardBioEdits,
  testimonial: discardTestimonialEdits
};

function cancelEdits() {
  CANCEL_BY_TAB[activeTab.value]?.();
}

// the footer's Cancel and OK: the bio and testimonial only (Settings' edits have their own)
const isEditOpen = computed(() => {
  if (activeTab.value === "settings" || activeTab.value === "events") return false;
  if (activeTab.value === "public") return isBioEditing.value;
  return isTestimonialEditing.value;
});

const isConfirmDisabled = computed(() => {
  if (isCommittingFields.value || isSaving.value) return true;
  if (activeTab.value === "public") return isBioOverLimit.value;
  return activeTab.value === "testimonial" && isTestimonialOverLimit.value;
});

// initial focus (useInitialFocus; FORMS.md § 17): not a workflow, so the top-right ✕, whichever
// tab it opens on (choosing a tab keeps the focus on the tab bar)
const dialogRef = ref(null);
const getTopClose = () => dialogRef.value?.$el?.querySelector?.("header .close") || null;
const isOpenAndSignedIn = () => getIsProfileHash(route.hash) && isSignedIn.value;
useInitialFocus(isOpenAndSignedIn, () => (isEditOpen.value ? null : getTopClose()));

// Close and Sign Out drop an open Settings edit, pending email removals included (as Cancel)
function closeDialog() {
  cancelSettingsEdits();
  router.push({path: route.path, hash: ""});
}

async function signOut() {
  cancelSettingsEdits();
  await window.Clerk?.signOut();
  closeDialog();
}

// opening the dialog loads the content, once signed in: a page opened on #profile (as after
// signing in) has no session for a moment, and the request was refused (401; 2026-10-18)
watch([() => getIsProfileHash(route.hash), () => isSignedIn.value], ([isOpen, signedIn]) => {
  if (isOpen && signedIn) loadContent();
}, {immediate: true});

// closing it tidies up (switching tabs does neither)
watch(() => getIsProfileHash(route.hash), (isOpen) => {
  if (isOpen) return;
  closeAllFields(); // dialog closed: fields close (their drafts stay in local storage)
  isEditingName.value = false; // likewise the name (draft kept)
  discardBioEdits(); // unsaved bio and testimonial changes are dropped
  discardTestimonialEdits();
  if (!route.hash.startsWith("#new-email")) discardEmails(); // adding an email: kept open
}, {immediate: true});

const nameInClerk = computed(() => {
  const {firstName, lastName} = user.value || {};
  return `${firstName || ""} ${lastName || ""}`.trim();
});

const emailInClerk = computed(() => {
  const {emailAddresses, primaryEmailAddressId} = user.value || {};
  if (!Array.isArray(emailAddresses) || !emailAddresses.length) return "";
  if (!primaryEmailAddressId) return emailAddresses[0]?.emailAddress || "";
  const primary = emailAddresses.find((item) => item?.id === primaryEmailAddressId);
  return primary?.emailAddress || emailAddresses[0]?.emailAddress || "";
});

const videoAccess = computed(() => getVideoAccessNow(user.value?.publicMetadata?.videoAccess));
// limited access (the Custom deal, a Press Pass, while it lasts): [{collection, year, until}],
// until in ms, one per collection
function getLimitedAccess(kind, field) {
  const temporary = user.value?.publicMetadata?.videoAccess?.[field];
  if (!getIsTemporaryValid(temporary, Date.now(), kind)) return null;
  const until = getTemporaryUntil(temporary, kind);
  return {collection: kind, year: Number(temporary.year), until};
}
const limited = computed(() => {
  const entries = Object.entries(TEMPORARY_FIELDS);
  return entries.map(([kind, field]) => getLimitedAccess(kind, field)).filter(Boolean);
});
const {lockedCounts, hasLockedVideos} = useLockedVideos(user);

// Unlock Videos: the page that sells access (/Videos/Unlock); Settings' edits are dropped, as Close
function openUnlock() {
  cancelSettingsEdits();
  router.push({path: "/Videos/Unlock", hash: ""});
}

const settingsRef = ref(null);
const dialogHeight = useSettingsHeight({
  isOpen: computed(() => getIsProfileHash(route.hash)), activeTab,
  isReady: computed(() => Boolean(emailInClerk.value)), settingsRef,
});

watch(activeTab, (tab) => {
  saveStatus.value = null;
  closeAllFields(); // Settings and Profile share the fields: an open one doesn't follow the tab
  if (tab === "public" || tab === "testimonial") loadContent();
});
</script>

<style scoped>
.header-left {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px; /* as the tabs' (TabNav.vue) */
  align-items: center;
}
.settings-tab,
.page {
  position: relative;
  min-height: 400px;
}
/* the gap between Settings' four parts (the name and picture, SUBSCRIPTIONS, PRIVACY, VIDEOS;
   Seán, 2026-10-20): the phone's compact 12px, a little more where the window has room to
   spare (up to 22px at about 1000px high); not to fill it. A small phone: 6px */
.settings-tab {
  --section-gap: max(12px, min(22px, calc((100dvh - 600px) / 18)));
}
/* Public and Testimonial: the bio card fills the dialog down to just above Sign Out / Close, and
   a long text scrolls inside it (RichTextEditor); the scroll area (.modal-main) has the dialog's
   fixed height, so 100% here is that height */
.tab-body {
  height: 100%;
}
/* room at the sides for the edit layers' shadow (the scroll area clips what's outside it); the
   negative margin keeps the content where it was (FORMS.md § 17, as for focus rings) */
:deep(.modal-main) {
  padding-inline: 16px;
  margin-inline: -16px;
}
.page.fills {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 4px; /* the top area a little tighter (2026-10-20: was 6px, 8px 0 10px) */
  padding: 0 0 4px 0;
}
/* one compact size at every screen (Seán, 2026-10-20: the phone's; it was 6rem, with the hint
   "Click items above to edit" under the email) */
.avatar {
  height: 4.5rem;
  width: 4.5rem;
  border: 1px solid #ccc;
}
.identity {
  position: relative; /* the homepage, LinkedIn and affiliation edit layers dock here */
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
/* above Sign Out and Close, the sections' own gap (Seán, 2026-10-23: 2px was too tight once the
   buttons grew); the dialog's height follows (useSettingsHeight.js) */
.settings {
  padding-bottom: var(--section-gap, 12px);
}
/* a laptop's short window (640px, 6247bdd4): Settings still fits without scrolling, the space
   round the buttons a little tighter there */
@media (max-height: 700px) {
  .settings {
    padding-bottom: 6px;
  }
  :deep(footer.hasContent) {
    padding-bottom: 18px;
  }
}
.footer-buttons.compact {
  position: relative;
}
/* the buttons: the dialog's (Dialog/Footer.vue; 2026-10-23). Sign Out, the secondary action:
   outlined in the dark green, no fill, so Close (or Unlock Videos) is the one that stands out
   (Seán's spec, 2026-10-23; outranks Footer.vue's call to action) */
.footer-buttons .footer-button.isSecondary {
  border: 1.5px solid var(--brand-dark-color);
  color: var(--brand-dark-color);
  background-color: transparent;
}
.footer-buttons .footer-button.isSecondary:hover {
  background-color: color-mix(in srgb, var(--brand-color) 10%, transparent);
}
.save-status {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
}
.save-status.success {
  color: var(--brand-color);
}
.save-status.error {
  color: #dc2204;
}
/* a small phone with its browser bars (under 600px): a smaller picture still */
@media (max-height: 600px) {
  .settings-tab {
    --section-gap: 6px;
  }
  .avatar {
    height: 3rem;
    width: 3rem;
  }
  .header {
    gap: 2px;
  }
}
</style>
